import { penQualityFromPens } from './penQuality';
import type { FeedingPen } from './penTypes';
import type { FeedingSummaryCoverage, FeedingSummaryResponse } from './summaryTypes';
import type { BioscopeDataQuality, FeedingSummary } from './types';

/**
 * A summary from a backend that predates `summary.coverage` was computed under rules the
 * page no longer shows: each Bioscope value averaged every pen with one, a collecting
 * pen's raw estimate included; bFCR was a mean of values already rounded to 2 dp; RGI was
 * a 0 dp mean of the pen ratios. Those values are withheld, not recomputed from the pens:
 * the pens carry their values rounded for display, so a recomputed bFCR or RGI can land a
 * rounding step from the true one and cross its target, and a second copy of the
 * backend's averaging rules would have to be kept in step with it. Only a response from
 * an older backend during a split or rolling deploy takes this path.
 */
type WithheldField =
    | 'avgWeightBioscopeG'
    | 'avgWeightBioscopeConfIntervalG'
    | 'fcrPms'
    | 'fcrBioscope'
    | 'fcrBioscopeConfInterval'
    | 'rgiFeed'
    | 'rgiBioscope'
    | 'sfrBioscopePct';

const WITHHELD: Pick<FeedingSummary, WithheldField> = {
    avgWeightBioscopeG: null,
    avgWeightBioscopeConfIntervalG: null,
    fcrPms: null,
    fcrBioscope: null,
    fcrBioscopeConfInterval: null,
    rgiFeed: null,
    rgiBioscope: null,
    sfrBioscopePct: null,
};

/**
 * Such a summary does not say which pens a value rests on, so every pen counts and no
 * card shows a note: a note built from a guess would name reasons that may be false.
 */
function everyPen(pens: FeedingPen[]): FeedingSummaryCoverage {
    const all = pens.map((pen) => pen.cageId);
    return {
        selectedPens: pens.length,
        weightBioscope: all,
        weightPmsPlan: all,
        weightPmsActual: all,
        fcrPms: all,
        fcrBioscope: all,
        rgiFeed: all,
        rgiBioscope: all,
        feedActual: all,
        feedPlan: all,
        fallthrough: all,
    };
}

/** The best pen, as the API rates the site now; the older summary sent the weakest. */
function bestQuality(pens: FeedingPen[]): BioscopeDataQuality {
    if (pens.some((pen) => pen.bioscopeDataQuality === 'ready')) return 'ready';
    return pens.some((pen) => pen.bioscopeDataQuality === 'collecting') ? 'collecting' : 'noData';
}

/** Fills an older backend's summary; see FeedingDashboardOverviewResponse. */
export function mapFeedingSummaryToDomain(
    summary: FeedingSummaryResponse,
    pens: FeedingPen[]
): FeedingSummary {
    const { coverage, penQuality, feedCoverage } = summary;
    if (coverage && penQuality) {
        return { ...summary, coverage, penQuality, feedCoverage: feedCoverage ?? null };
    }
    return {
        ...summary,
        ...WITHHELD,
        bioscopeDataQuality: bestQuality(pens),
        coverage: everyPen(pens),
        penQuality: penQualityFromPens(pens),
        feedCoverage: feedCoverage ?? null,
    };
}
